// Inventory & Assets — Phase 1
// Tabs shipped in this phase: Overview, Asset Register, Invoice / Purchase.
// Phase 2 will add Stock, Movement, Verification; Phase 3 = Exit Handover
// & Reports & Audit UI.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
  from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, Plus, Upload, Download, Archive, Package, Trash2, Undo2, Landmark, Boxes, Wheat, Coins, Wrench, SearchX, FileWarning, ShieldCheck, ArrowLeft, ChevronUp, ChevronDown } from "lucide-react";
import { MetalKpi, ViewSwitch } from "@/components/foodcost/FoodCostUI";
import { InvCategoryBlocks, AssetCards } from "@/components/inventory/InvUI";
import { VerificationPanel, MovementPanel, RepairPanel } from "./inventory/Phase2Panels";
import { ExitPanel, ReportsPanel, AuditPanel } from "./inventory/Phase3Panels";
import { CategoryManagerPanel } from "./inventory/CategoryManagerPanel";

const API = process.env.REACT_APP_BACKEND_URL;

const post = async (path, token, body = {}) => {
  const r = await fetch(`${API}/api/inventory${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, ...body }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
};

const money = (v) => `₹${Number(v || 0).toLocaleString(undefined,
  { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function InventoryAssets() {
  const { session } = useAuth();
  const token = session?.token;
  const [tab, setTab] = useState("overview");
  const [center, setCenter] = useState(session?.center || "PB-HSR");
  const [centers, setCenters] = useState([]);
  const [catData, setCatData] = useState({ categories: [], conditions: [], statuses: [] });

  useEffect(() => {
    // Load centres list (uses existing /api/centers endpoint)
    fetch(`${API}/api/centers`).then(r => r.ok ? r.json() : { centers: [] })
      .then(d => setCenters(d.centers || [])).catch(() => {});
    post("/categories/list", token).then(setCatData).catch((e) => toast.error(e.message));
  }, [token]);

  const isSuperAdmin = !!session?.is_super_admin;
  const isAdmin = isSuperAdmin || session?.role === "Admin" || session?.role === "Super Admin";

  return (
    <div className="space-y-5 inv-soft" data-testid="inventory-assets-page">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-[#e2c27a] bg-gradient-to-r from-[#fffaf0] to-[#fbf0d6] p-4">
        <div className="flex items-center gap-3">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl border-2 border-[#d9ae52] bg-[#750817] text-[#F4D58D]">
            <Package className="w-8 h-8" />
          </span>
          <div>
            <h1 className="text-3xl font-bold text-[#750817]" data-testid="inv-page-title">Inventory &amp; Assets</h1>
            <div className="text-sm text-[#5a3a22]">
              Centre-wise inventory for equipment, utensils, cutlery, furniture, electrical, uniforms,
              housekeeping, packaging, and live raw material stock.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm font-semibold">Centre</Label>
          <Select value={center} onValueChange={setCenter}>
            <SelectTrigger className="w-44 h-11 text-base" data-testid="inv-center-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(centers.length ? centers : [{ code: center }]).map(c => (
                <SelectItem key={c.code || c} value={c.code || c}>{c.code || c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="inv-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="register" data-testid="inv-tab-register">Asset Register</TabsTrigger>
          <TabsTrigger value="invoice" data-testid="inv-tab-invoice">Invoice / Purchase</TabsTrigger>
          <TabsTrigger value="verify" data-testid="inv-tab-verify">Physical Verify</TabsTrigger>
          <TabsTrigger value="movement" data-testid="inv-tab-movement">Movement</TabsTrigger>
          <TabsTrigger value="repair" data-testid="inv-tab-repair">Repairs</TabsTrigger>
          <TabsTrigger value="reports" data-testid="inv-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="exit" data-testid="inv-tab-exit">Exit Handover</TabsTrigger>
          <TabsTrigger value="audit" data-testid="inv-tab-audit">Audit Log</TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="catman" data-testid="inv-tab-catman">Categories</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview">
          <OverviewPanel token={token} center={center} isSuperAdmin={isSuperAdmin}
            categories={catData.categories} conditions={catData.conditions} statuses={catData.statuses} />
        </TabsContent>

        <TabsContent value="register">
          <RegisterPanel token={token} center={center}
            categories={catData.categories} conditions={catData.conditions}
            statuses={catData.statuses} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />
        </TabsContent>

        <TabsContent value="invoice">
          <InvoicePanel token={token} center={center} />
        </TabsContent>

        <TabsContent value="verify">
          <VerificationPanel token={token} center={center}
            categories={catData.categories} conditions={catData.conditions}
            isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="movement">
          <MovementPanel token={token} center={center} centers={centers} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="repair">
          <RepairPanel token={token} center={center} />
        </TabsContent>

        <TabsContent value="reports">
          <ReportsPanel token={token} center={center} />
        </TabsContent>

        <TabsContent value="exit">
          <ExitPanel token={token} center={center}
            isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />
        </TabsContent>

        <TabsContent value="audit">
          <AuditPanel token={token} center={center} />
        </TabsContent>

        <TabsContent value="catman">
          <CategoryManagerPanel token={token} isSuperAdmin={isSuperAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


/* ============================================================
 * Overview
 * ============================================================ */
function OverviewPanel({ token, center, isSuperAdmin, categories, conditions, statuses }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [pick, setPick] = useState("");
  const [assets, setAssets] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [showSummary, setShowSummary] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await post("/overview", token, { center })); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token, center]);
  useEffect(() => { load(); }, [load]);

  const loadAssets = useCallback(async () => {
    try { setAssets((await post("/assets/list", token, { center })).assets || []); }
    catch (e) { toast.error(e.message); }
  }, [token, center]);
  useEffect(() => { setPick(""); setAssets(null); }, [center]);
  useEffect(() => { if (pick && !assets) loadAssets(); }, [pick, assets, loadAssets]);

  if (!data) return <div className="p-6 text-center">
    {loading ? <Loader2 className="w-5 h-5 animate-spin inline" /> : "No data"}
  </div>;

  const t = data.totals; const h = data.health;
  const pc = data.per_category || {};
  const catName = (code) => categories.find(c => c.code === code)?.name || code;
  const blocks = categories.map(c => {
    const raw = c.is_raw_material || c.code === "RAW_MATERIAL";
    const row = raw ? { count: data.raw_material_count, value: t.raw_material_value } : (pc[c.code] || { count: 0, quantity: 0, value: 0 });
    return { code: c.code, name: c.name, count: row.count || 0,
      sub: `${raw ? "Live stock" : `Qty ${row.quantity || 0}`} · ${money(row.value)}` };
  });
  const status = (n, bad) => (n > 0 ? bad : "All clear");
  const kpis = [
    ["Fixed Assets Value", money(t.fixed_assets_value), "fixed", Landmark, "Equipment & durable assets"],
    ["Consumable Value", money(t.consumable_value), "consumable", Boxes, "Consumable stock"],
    ["Raw Material Value", money(t.raw_material_value), "raw", Wheat, "Live from Consumption"],
    ["Combined Inventory Value", money(t.combined_value), "combined", Coins, "Total inventory"],
    ["Repair Required", h.repair_required, "repair-required", Wrench, status(h.repair_required, "Needs attention")],
    ["Missing Assets", h.missing, "missing", SearchX, status(h.missing, "Investigate")],
    ["Without Invoice", h.no_invoice, "no-invoice", FileWarning, status(h.no_invoice, "Link purchase invoice")],
    [`Not Verified (>${h.not_verified_window_days}d)`, h.not_verified, "not-verified", ShieldCheck, status(h.not_verified, "Run physical verification")],
  ];
  const picked = (assets || []).filter(r => r.category_code === pick);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="inv-kpi-blocks">
        {kpis.map(([l, v, id, I, sub], i) => <MetalKpi key={id} i={i} label={l} value={v} sub={sub} Icon={I} testid={`inv-tile-${id}`} />)}
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-bold text-[#750817] flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            <Package className="w-5 h-5" /> Inventory by Category</h2>
          {pick && <button type="button" className="bi-pill" onClick={() => setPick("")} data-testid="inv-ov-back">
            <ArrowLeft className="h-4 w-4" />Back to All Categories</button>}
        </div>
        <InvCategoryBlocks items={blocks} value={pick} onChange={(c) => setPick(c === pick ? "" : c)} testid="inv-ov-cat" />
        {pick && (
          <div className="space-y-2" data-testid="inv-ov-cat-assets">
            <h3 className="text-lg font-bold text-[#750817]">{catName(pick)} — {assets ? picked.length : "…"} asset(s)</h3>
            {assets ? <AssetCards rows={picked} catName={catName} onView={setViewing} onEdit={setEditing} />
              : <Loader2 className="w-5 h-5 animate-spin" />}
          </div>)}
      </section>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <button type="button" className="flex items-center gap-2" onClick={() => setShowSummary(!showSummary)} data-testid="inv-ov-summary-toggle">
            <CardTitle className="text-lg">Category Summary</CardTitle>
            {showSummary ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {isSuperAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}
              data-testid="inv-open-settings">
              Verify-Window Settings
            </Button>
          )}
        </CardHeader>
        {showSummary && <CardContent>
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Items</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              <TableHead className="text-right">Value</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {Object.entries(pc).map(([code, row]) => (
                <TableRow key={code}>
                  <TableCell className="font-medium">{code}</TableCell>
                  <TableCell className="text-right">{row.count}</TableCell>
                  <TableCell className="text-right">{row.quantity}</TableCell>
                  <TableCell className="text-right">{money(row.value)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/30">
                <TableCell className="font-medium">RAW_MATERIAL (live from Consumption)</TableCell>
                <TableCell className="text-right">{data.raw_material_count}</TableCell>
                <TableCell className="text-right">—</TableCell>
                <TableCell className="text-right">{money(t.raw_material_value)}</TableCell>
              </TableRow>
            </TableBody>
          </Table></div>
        </CardContent>}
      </Card>

      {showSettings && <VerifyWindowDialog token={token} onClose={() => { setShowSettings(false); load(); }} />}
      {viewing && <AssetViewDialog asset={viewing} catName={catName} onClose={() => setViewing(null)} />}
      {editing && <AssetEditorDialog asset={editing} center={center} token={token}
        categories={categories} conditions={conditions} statuses={statuses}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); setAssets(null); load(); }} />}
    </div>
  );
}


function AssetViewDialog({ asset: a, catName, onClose }) {
  const rows = [["Asset Code", a.asset_code], ["Category", `${catName(a.category_code)}${a.subcategory ? ` / ${a.subcategory}` : ""}`],
    ["Quantity", `${a.quantity_current} ${a.unit || ""}`], ["Condition", a.condition], ["Status", a.status], ["Rate", money(a.purchase_rate)],
    ["Value", money(a.current_asset_value)], ["Vendor", a.vendor], ["Invoice No.", a.invoice_number], ["Serial No.", a.serial_number],
    ["Location", a.location], ["Responsible", a.responsible_person], ["Last Verified", a.last_verified_date ? String(a.last_verified_date).slice(0, 10) : ""],
    ["Warranty Expiry", a.warranty_expiry], ["Remarks", a.remarks]];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inv-asset-view-dialog">
        <DialogHeader><DialogTitle>{a.asset_name}</DialogTitle></DialogHeader>
        <div className="divide-y divide-[#f1e2bd]">
          {rows.map(([l, v]) => <div key={l} className="flex justify-between gap-3 py-1.5 text-base">
            <span className="text-[#5a3a22]">{l}</span><b className="text-right break-words">{v || "—"}</b></div>)}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="inv-asset-view-close">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function VerifyWindowDialog({ token, onClose }) {
  const [days, setDays] = useState(90);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    post("/settings/get", token).then(d => setDays(d.not_verified_window_days)).catch(() => {});
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await post("/settings/update", token, { not_verified_window_days: Number(days) });
      toast.success("Settings updated");
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inv-verify-window-dialog">
        <DialogHeader><DialogTitle>Not-Verified Window</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Days after which an asset is flagged as &quot;not verified&quot;</Label>
          <Input type="number" min="7" max="730" value={days}
            onChange={(e) => setDays(e.target.value)} data-testid="inv-settings-days" />
          <div className="text-xs text-muted-foreground">
            Any asset whose <b>last verified date</b> is older than this many days is counted in the
            &quot;Not Verified&quot; tile so managers know to run a physical verification.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="inv-settings-save">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ============================================================
 * Asset Register (grid, filters, add / edit / archive)
 * ============================================================ */
function RegisterPanel({ token, center, categories, conditions, statuses, isAdmin, isSuperAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ category_code: "", condition: "", status: "",
                                             search: "", include_archived: false });
  const [editing, setEditing] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const canBulkArchive = isAdmin; // Super Admin + Admin only; Center Manager blocked
  const showingArchived = !!filters.include_archived;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = { center, ...filters };
      delete q.category_code;
      Object.keys(q).forEach((k) => { if (q[k] === "" || q[k] === undefined) delete q[k]; });
      const d = await post("/assets/list", token, q);
      setRows(d.assets || []);
      setSelectedIds(new Set()); // reset selection when list changes
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token, center, filters.condition, filters.status, filters.search, filters.include_archived]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedIds(new Set()); }, [filters.category_code]);

  const [view, setView] = useState("both");
  const [gridOpen, setGridOpen] = useState(true);
  const [viewing, setViewing] = useState(null);
  const visible = useMemo(() => (filters.category_code ? rows.filter(r => r.category_code === filters.category_code) : rows), [rows, filters.category_code]);
  const catName = (code) => categories.find(c => c.code === code)?.name || code;
  const counts = useMemo(() => rows.reduce((m, r) => ({ ...m, [r.category_code]: (m[r.category_code] || 0) + 1 }), {}), [rows]);
  const blocks = [{ code: "", name: "All Assets", count: rows.length },
    ...categories.map(c => ({ code: c.code, name: c.name, count: counts[c.code] || 0 }))];

  // Selectable rows (skip raw-material virtual rows)
  const selectableRows = useMemo(() => visible.filter(r => !r.is_raw_material_link), [visible]);
  const allVisibleSelected = selectableRows.length > 0 &&
    selectableRows.every(r => selectedIds.has(r.asset_id));
  const someVisibleSelected = !allVisibleSelected &&
    selectableRows.some(r => selectedIds.has(r.asset_id));

  const toggleRow = (aid) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(aid)) n.delete(aid); else n.add(aid);
      return n;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) {
        const n = new Set(prev);
        selectableRows.forEach(r => n.delete(r.asset_id));
        return n;
      }
      const n = new Set(prev);
      selectableRows.forEach(r => n.add(r.asset_id));
      return n;
    });
  };

  const downloadTemplate = async () => {
    try {
      const r = await fetch(`${API}/api/inventory/excel/template`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!r.ok) throw new Error("Template download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Inventory-Assets-Template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const archive = async (row) => {
    if (row.is_raw_material_link) return;
    if (!window.confirm(`Archive "${row.asset_name}"?`)) return;
    try {
      await post("/assets/delete", token, { asset_id: row.asset_id, hard_delete: false });
      toast.success("Archived"); load();
    } catch (e) { toast.error(e.message); }
  };

  const del = async (row) => {
    if (row.is_raw_material_link) return;
    if (!window.confirm(`Hard-delete "${row.asset_name}"? Only allowed if no linked history.`)) return;
    try {
      await post("/assets/delete", token, { asset_id: row.asset_id, hard_delete: true });
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e.message); }
  };

  const bulkRestore = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Restore ${ids.length} archived asset(s) to Active?`)) return;
    try {
      const d = await post("/assets/bulk-restore", token, { asset_ids: ids });
      toast.success(`Restored ${d.restored_count}${d.skipped_count ? ` (skipped ${d.skipped_count})` : ""}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
    <section className="space-y-3" data-testid="inv-reg-category-area">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-bold text-[#750817]" style={{ fontFamily: "'Playfair Display', serif" }}>
          {filters.category_code ? catName(filters.category_code) : "All Assets"} <span className="text-base font-semibold text-[#5a3a22]">({visible.length})</span></h2>
        <div className="flex flex-wrap gap-2">
          {filters.category_code && <button type="button" className="bi-pill" onClick={() => setFilters({ ...filters, category_code: "" })} data-testid="inv-reg-back">
            <ArrowLeft className="h-4 w-4" />Back to All Categories</button>}
          <ViewSwitch view={view} setView={setView} testid="inv-reg-view" />
        </div>
      </div>
      <InvCategoryBlocks items={blocks} value={filters.category_code} onChange={(c) => setFilters({ ...filters, category_code: c })} testid="inv-reg-cat" />
      {view !== "grid" && (loading && !rows.length ? <Loader2 className="w-5 h-5 animate-spin" />
        : <AssetCards key={`${filters.category_code}|${rows.length}`} rows={visible} catName={catName} onView={setViewing} onEdit={setEditing} />)}
    </section>
    {view !== "cards" && <Card data-testid="inv-reg-grid-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="bi-pill" onClick={() => setGridOpen(!gridOpen)} data-testid="inv-reg-grid-toggle">
            {gridOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}{gridOpen ? "Collapse grid" : "Expand grid"}</button>
          <Input placeholder="Search asset / code / vendor…" className="w-72"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="inv-filter-search" />
          <Select value={filters.category_code || "ALL"}
            onValueChange={(v) => setFilters({ ...filters, category_code: v === "ALL" ? "" : v })}>
            <SelectTrigger className="w-52" data-testid="inv-filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All categories</SelectItem>
              {categories.map(c => <SelectItem key={c.code} value={c.code}>{c.letter}. {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.condition || "ALL"}
            onValueChange={(v) => setFilters({ ...filters, condition: v === "ALL" ? "" : v })}>
            <SelectTrigger className="w-40" data-testid="inv-filter-condition"><SelectValue placeholder="All conditions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All conditions</SelectItem>
              {conditions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status || "ALL"}
            onValueChange={(v) => setFilters({ ...filters, status: v === "ALL" ? "" : v })}>
            <SelectTrigger className="w-36" data-testid="inv-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer"
            data-testid="inv-toggle-show-archived">
            <Checkbox checked={showingArchived}
              onCheckedChange={(v) => setFilters({ ...filters, include_archived: !!v })} />
            Show Archived
          </label>
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="inv-download-template">
            <Download className="w-4 h-4 mr-1" /> Excel Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowUpload(true)} data-testid="inv-open-upload">
            <Upload className="w-4 h-4 mr-1" /> Bulk Upload
          </Button>
          {canBulkArchive && !showingArchived && (
            <Button variant="outline" size="sm"
              className="border-amber-500 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              disabled={selectedIds.size === 0}
              onClick={() => setShowBulkDialog(true)}
              data-testid="inv-bulk-delete-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Bulk Delete
            </Button>
          )}
          {canBulkArchive && showingArchived && (
            <Button variant="outline" size="sm"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
              disabled={selectedIds.size === 0}
              onClick={bulkRestore}
              data-testid="inv-bulk-restore-btn">
              <Undo2 className="w-4 h-4 mr-1" /> Bulk Restore
            </Button>
          )}
          <Button size="sm" onClick={() => setEditing({})} data-testid="inv-add-asset">
            <Plus className="w-4 h-4 mr-1" /> New Asset
          </Button>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      {gridOpen && <CardContent>
        <div className="text-sm text-muted-foreground mb-2 flex items-center gap-3">
          <span data-testid="inv-reg-row-count">{visible.length} row(s)</span>
          {selectedIds.size > 0 && (
            <span className="font-medium text-foreground" data-testid="inv-selected-count">
              Selected: {selectedIds.size} asset{selectedIds.size === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              {canBulkArchive && (
                <TableHead className="w-8">
                  <Checkbox
                    checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                    onCheckedChange={toggleAllVisible}
                    aria-label="Select All Visible"
                    data-testid="inv-select-all-visible" />
                </TableHead>
              )}
              <TableHead>Code</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead>Condition</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Responsible</TableHead>
              <TableHead>Last Verified</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {visible.map(r => (
                <TableRow key={r.asset_id} data-testid={`inv-row-${r.asset_id}`}>
                  {canBulkArchive && (
                    <TableCell className="w-8">
                      {!r.is_raw_material_link && (
                        <Checkbox
                          checked={selectedIds.has(r.asset_id)}
                          onCheckedChange={() => toggleRow(r.asset_id)}
                          aria-label={`Select ${r.asset_name}`}
                          data-testid={`inv-select-${r.asset_id}`} />
                      )}
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-xs">{r.asset_code}</TableCell>
                  <TableCell className="font-medium">
                    {r.asset_name}
                    {r.is_raw_material_link && <Badge variant="outline" className="ml-2">RAW</Badge>}
                  </TableCell>
                  <TableCell>{r.category_code}{r.subcategory ? ` / ${r.subcategory}` : ""}</TableCell>
                  <TableCell>{r.unit}</TableCell>
                  <TableCell className="text-right">{r.quantity_current}</TableCell>
                  <TableCell>{r.condition}</TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell className="text-right">{money(r.purchase_rate)}</TableCell>
                  <TableCell className="text-right font-semibold">{money(r.current_asset_value)}</TableCell>
                  <TableCell>{r.vendor || "—"}</TableCell>
                  <TableCell>{r.location || "—"}</TableCell>
                  <TableCell>{r.responsible_person || "—"}</TableCell>
                  <TableCell className="text-xs">{r.last_verified_date ? String(r.last_verified_date).slice(0, 10) : "—"}</TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    {!r.is_raw_material_link && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditing(r)}
                          data-testid={`inv-edit-${r.asset_id}`}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => archive(r)}
                          data-testid={`inv-archive-${r.asset_id}`}>
                          <Archive className="w-4 h-4" />
                        </Button>
                        {isSuperAdmin && (
                          <Button size="sm" variant="ghost" onClick={() => del(r)}
                            data-testid={`inv-delete-${r.asset_id}`}>×</Button>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && visible.length === 0 && (
                <TableRow><TableCell colSpan={canBulkArchive ? 15 : 14} className="text-center text-muted-foreground py-6">
                  No assets. Click <b>New Asset</b> or <b>Bulk Upload</b> to get started.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>}
    </Card>}

      {viewing && <AssetViewDialog asset={viewing} catName={catName} onClose={() => setViewing(null)} />}
      {editing && <AssetEditorDialog asset={editing} center={center} token={token}
        categories={categories} conditions={conditions} statuses={statuses}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }} />}

      {showUpload && <ExcelUploadDialog token={token} center={center}
        onClose={() => { setShowUpload(false); load(); }} />}

      {showBulkDialog && (
        <BulkArchiveDialog
          token={token}
          center={center}
          rows={visible}
          filters={filters}
          selectedIds={selectedIds}
          onClose={() => setShowBulkDialog(false)}
          onDone={() => { setShowBulkDialog(false); load(); }}
        />
      )}
    </div>
  );
}


/* ============================================================
 * Bulk Archive Dialog (Safe Bulk Delete)
 * ============================================================ */
const BULK_ARCHIVE_REASONS = [
  "Duplicate records",
  "Wrong bulk upload",
  "Asset no longer applicable",
  "Data cleanup",
  "Centre setup correction",
  "Other",
];

function BulkArchiveDialog({ token, center, rows, filters, selectedIds, onClose, onDone }) {
  const selectableRows = useMemo(() => rows.filter(r => !r.is_raw_material_link), [rows]);
  const selectedRows = useMemo(
    () => selectableRows.filter(r => selectedIds.has(r.asset_id)),
    [selectableRows, selectedIds]
  );
  const filteredCount = selectableRows.length; // "all filtered" = every non-raw visible row

  const [mode, setMode] = useState("selected"); // "selected" | "filtered"
  const [reason, setReason] = useState(BULK_ARCHIVE_REASONS[0]);
  const [remarks, setRemarks] = useState("");
  const [typedConfirm, setTypedConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const targetIds = mode === "filtered"
    ? selectableRows.map(r => r.asset_id)
    : selectedRows.map(r => r.asset_id);
  const targetCount = targetIds.length;
  const previewNames = (mode === "filtered" ? selectableRows : selectedRows).slice(0, 5)
    .map(r => r.asset_name);

  const remarksInvalid = reason === "Other" && !remarks.trim();
  const filteredConfirmInvalid = mode === "filtered" &&
    typedConfirm.trim().toUpperCase() !== `DELETE ${filteredCount}`;
  const disabled = busy || targetCount === 0 || remarksInvalid || filteredConfirmInvalid;

  const doArchive = async () => {
    setBusy(true);
    try {
      const d = await post("/assets/bulk-archive", token, {
        asset_ids: targetIds, reason, remarks: remarks.trim() || undefined,
      });
      const msg = `Archived ${d.archived_count}${d.skipped_count ? ` (skipped ${d.skipped_count})` : ""}`;
      toast.success(msg);
      if (d.skipped_count) {
        const first = (d.skipped || []).slice(0, 3)
          .map(x => `${x.asset_name || x.asset_id}: ${x.reason}`).join(" · ");
        if (first) toast.warning(`Skipped — ${first}${d.skipped_count > 3 ? "…" : ""}`);
      }
      onDone();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="inv-bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-700">
            DELETE {targetCount} ASSET{targetCount === 1 ? "" : "S"}?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded border p-3 bg-amber-50/50 space-y-1">
            <div>This will <b>archive</b> (soft-delete) the selected assets from this centre.
              Records are preserved for audit and can be restored via <b>Show Archived</b>.</div>
            <div><b>Centre:</b> {center}</div>
            <div><b>Selected Assets:</b> {targetCount}</div>
            {previewNames.length > 0 && (
              <ul className="list-disc list-inside text-xs text-muted-foreground pl-1">
                {previewNames.map((n, i) => <li key={i}>{n}</li>)}
                {targetCount > previewNames.length && (
                  <li>…and {targetCount - previewNames.length} more</li>
                )}
              </ul>
            )}
          </div>

          {filteredCount > selectedRows.length && (
            <div className="flex items-center gap-4 text-xs">
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="radio" checked={mode === "selected"}
                  onChange={() => setMode("selected")}
                  data-testid="inv-bulk-mode-selected" />
                Delete Selected ({selectedRows.length})
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="radio" checked={mode === "filtered"}
                  onChange={() => setMode("filtered")}
                  data-testid="inv-bulk-mode-filtered" />
                Delete All Filtered ({filteredCount})
              </label>
            </div>
          )}

          <div>
            <Label>Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="inv-bulk-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BULK_ARCHIVE_REASONS.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Remarks {reason === "Other" && <span className="text-red-600">*</span>}</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
              placeholder={reason === "Other" ? "Required when reason is Other" : "Optional"}
              data-testid="inv-bulk-remarks" />
          </div>

          {mode === "filtered" && (
            <div>
              <Label className="text-red-700">
                Type <span className="font-mono">DELETE {filteredCount}</span> to confirm
              </Label>
              <Input value={typedConfirm} onChange={(e) => setTypedConfirm(e.target.value)}
                placeholder={`DELETE ${filteredCount}`}
                data-testid="inv-bulk-typed-confirm" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="inv-bulk-cancel">
            Cancel
          </Button>
          <Button variant="destructive" onClick={doArchive} disabled={disabled}
            data-testid="inv-bulk-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Archive {targetCount}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




function AssetEditorDialog({ asset, center, token, categories, conditions, statuses, onClose, onSaved }) {
  const [f, setF] = useState({
    asset_id: asset.asset_id || null,
    center: asset.center || center,
    category_code: asset.category_code || categories[0]?.code || "KITCHEN_EQUIPMENT",
    subcategory: asset.subcategory || "",
    asset_name: asset.asset_name || "",
    unit: asset.unit || "nos",
    quantity_current: asset.quantity_current ?? 0,
    condition: asset.condition || "Good",
    status: asset.status || "Active",
    purchase_rate: asset.purchase_rate ?? 0,
    purchase_date: asset.purchase_date || "",
    vendor: asset.vendor || "",
    invoice_number: asset.invoice_number || "",
    serial_number: asset.serial_number || "",
    warranty_expiry: asset.warranty_expiry || "",
    location: asset.location || "",
    responsible_person: asset.responsible_person || "",
    remarks: asset.remarks || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const activeCat = categories.find(c => c.code === f.category_code);

  const save = async () => {
    if (!f.asset_name.trim()) return toast.error("Asset name is required");
    setSaving(true);
    try {
      await post("/assets/save", token, f);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="inv-editor">
        <DialogHeader><DialogTitle>{f.asset_id ? "Edit Asset" : "New Asset"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Asset Name *</Label>
            <Input value={f.asset_name} onChange={(e) => set("asset_name", e.target.value)}
              data-testid="inv-editor-name" /></div>
          <div><Label>Unit</Label>
            <Input value={f.unit} onChange={(e) => set("unit", e.target.value)} /></div>
          <div><Label>Category</Label>
            <Select value={f.category_code} onValueChange={(v) => { set("category_code", v); set("subcategory", ""); }}>
              <SelectTrigger data-testid="inv-editor-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.filter(c => c.code !== "RAW_MATERIAL").map(c =>
                  <SelectItem key={c.code} value={c.code}>{c.letter}. {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Subcategory *</Label>
            <Select value={f.subcategory || "NONE"} onValueChange={(v) => set("subcategory", v === "NONE" ? "" : v)}>
              <SelectTrigger data-testid="inv-editor-subcategory">
                <SelectValue placeholder="Pick from list" />
              </SelectTrigger>
              <SelectContent>
                {(activeCat?.subcategories || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                {/* Preserve historical (deactivated) subcategory if the asset currently has one */}
                {f.subcategory && !(activeCat?.subcategories || []).includes(f.subcategory) && (
                  <SelectItem value={f.subcategory}>
                    {f.subcategory} (historical)
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {f.subcategory && !(activeCat?.subcategories || []).includes(f.subcategory) && (
              <div className="text-xs text-amber-600 mt-1">
                This subcategory is deactivated. Save to keep it, or pick an active one.
              </div>
            )}
          </div>
          <div><Label>Quantity</Label>
            <Input type="number" value={f.quantity_current}
              onChange={(e) => set("quantity_current", e.target.value)} data-testid="inv-editor-qty" />
          </div>
          <div><Label>Purchase Rate</Label>
            <Input type="number" value={f.purchase_rate}
              onChange={(e) => set("purchase_rate", e.target.value)} data-testid="inv-editor-rate" /></div>
          <div><Label>Condition</Label>
            <Select value={f.condition} onValueChange={(v) => set("condition", v)}>
              <SelectTrigger data-testid="inv-editor-condition"><SelectValue /></SelectTrigger>
              <SelectContent>{conditions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Vendor</Label>
            <Input value={f.vendor} onChange={(e) => set("vendor", e.target.value)} /></div>
          <div><Label>Invoice Number</Label>
            <Input value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} /></div>
          <div><Label>Purchase Date</Label>
            <Input type="date" value={f.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} /></div>
          <div><Label>Warranty Expiry</Label>
            <Input type="date" value={f.warranty_expiry} onChange={(e) => set("warranty_expiry", e.target.value)} /></div>
          <div><Label>Serial Number</Label>
            <Input value={f.serial_number} onChange={(e) => set("serial_number", e.target.value)} /></div>
          <div><Label>Location in Centre</Label>
            <Input value={f.location} onChange={(e) => set("location", e.target.value)} /></div>
          <div className="col-span-2"><Label>Responsible Person</Label>
            <Input value={f.responsible_person} onChange={(e) => set("responsible_person", e.target.value)} /></div>
          <div className="col-span-2"><Label>Remarks</Label>
            <Textarea rows={2} value={f.remarks} onChange={(e) => set("remarks", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="inv-editor-save">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ExcelUploadDialog({ token, center, onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    if (!file) return toast.error("Select a file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("token", token); fd.append("center", center); fd.append("file", file);
      const r = await fetch(`${API}/api/inventory/excel/preview`, { method: "POST", body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      setPreview(await r.json());
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const rows = [...(preview.valid || []), ...(preview.updates || [])];
      const r = await post("/excel/confirm", token, { center, rows });
      toast.success(`Imported — created ${r.created}, updated ${r.updated}`);
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="inv-upload-dialog">
        <DialogHeader><DialogTitle>Bulk Upload — {center}</DialogTitle></DialogHeader>
        {!preview ? (
          <div className="space-y-3">
            <input type="file" accept=".xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="inv-upload-file" />
            <div className="text-xs text-muted-foreground">
              Download the template from the Asset Register, fill each worksheet, then upload here.
              You&apos;ll see a validation preview before anything is written.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={doPreview} disabled={!file || busy} data-testid="inv-upload-preview">
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Validate
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Summary tiles */}
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              <div className="p-2 rounded bg-slate-500/10 text-center">
                <div className="text-xl font-bold">{preview.counts.total ?? "—"}</div>
                <div className="text-[10px] uppercase text-muted-foreground">Total Rows</div>
              </div>
              <div className="p-2 rounded bg-emerald-500/10 text-center">
                <div className="text-xl font-bold text-emerald-500">{preview.counts.valid}</div>
                <div className="text-[10px] uppercase text-muted-foreground">New</div>
              </div>
              <div className="p-2 rounded bg-blue-500/10 text-center">
                <div className="text-xl font-bold text-blue-500">{preview.counts.updates}</div>
                <div className="text-[10px] uppercase text-muted-foreground">Update</div>
              </div>
              <div className="p-2 rounded bg-amber-500/10 text-center">
                <div className="text-xl font-bold text-amber-500">{preview.counts.duplicates}</div>
                <div className="text-[10px] uppercase text-muted-foreground">Duplicates</div>
              </div>
              <div className="p-2 rounded bg-yellow-500/10 text-center">
                <div className="text-xl font-bold text-yellow-600">{preview.counts.warnings || 0}</div>
                <div className="text-[10px] uppercase text-muted-foreground">Warnings</div>
              </div>
              <div className="p-2 rounded bg-orange-500/10 text-center">
                <div className="text-xl font-bold text-orange-500">{preview.counts.unclassified || 0}</div>
                <div className="text-[10px] uppercase text-muted-foreground">Unclassified</div>
              </div>
              <div className="p-2 rounded bg-red-500/10 text-center">
                <div className="text-xl font-bold text-red-500">{preview.counts.errors}</div>
                <div className="text-[10px] uppercase text-muted-foreground">Errors</div>
              </div>
            </div>

            {preview.unknown_sheets?.length > 0 && (
              <div className="border border-red-500/40 rounded p-2 text-xs bg-red-500/5">
                <b className="text-red-500">Unknown Worksheet(s) — skipped:</b>
                <div>{preview.unknown_sheets.join(", ")}</div>
                <div className="text-muted-foreground mt-1">
                  Rename the sheet to match a category (Kitchen Equipment, Cutlery &amp; Crockery, etc.)
                  or ask an Admin to map it.
                </div>
              </div>
            )}

            {/* Detailed row grid — colour coded */}
            <div className="border rounded max-h-[380px] overflow-auto text-xs" data-testid="inv-upload-grid">
              <table className="w-full">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left p-2">Sheet</th>
                    <th className="text-left p-2">Row</th>
                    <th className="text-left p-2">Asset</th>
                    <th className="text-left p-2">Category</th>
                    <th className="text-left p-2">Sub</th>
                    <th className="text-left p-2">Unit</th>
                    <th className="text-right p-2">Qty</th>
                    <th className="text-right p-2">Rate</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Issue</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ...(preview.updates || []).map(r => ({ ...r, _kind: "Update" })),
                    ...(preview.valid || []).map(r => ({ ...r, _kind: "New" })),
                    ...(preview.warnings || []).map(r => ({ ...r, _kind: "Warning" })),
                    ...(preview.unclassified || []).map(r => ({ ...r, _kind: "Unclassified" })),
                    ...(preview.duplicates || []).map(r => ({ ...r, _kind: "Duplicate" })),
                    ...(preview.errors || []).map(r => ({ ...r, _kind: "Error" })),
                  ].slice(0, 500).map((r, i) => (
                    <tr key={i} className={
                      r._kind === "Error" ? "bg-red-500/5"
                      : r._kind === "Warning" ? "bg-yellow-500/5"
                      : r._kind === "Unclassified" ? "bg-orange-500/5"
                      : r._kind === "Duplicate" ? "bg-amber-500/5"
                      : r._kind === "Update" ? "bg-blue-500/5"
                      : "bg-emerald-500/5"
                    }>
                      <td className="p-1.5">{r.sheet || "—"}</td>
                      <td className="p-1.5">{r.row ?? "—"}</td>
                      <td className="p-1.5">{r.asset_name || r.item_name || "—"}</td>
                      <td className="p-1.5">{r.category_code || "—"}</td>
                      <td className="p-1.5">{r.subcategory || "—"}</td>
                      <td className="p-1.5">{r.unit || "—"}</td>
                      <td className="p-1.5 text-right">{r.quantity ?? "—"}</td>
                      <td className="p-1.5 text-right">{r.purchase_rate ?? "—"}</td>
                      <td className="p-1.5">
                        <span className={
                          r._kind === "Error" ? "text-red-500 font-medium"
                          : r._kind === "Warning" || r._kind === "Unclassified" ? "text-yellow-600"
                          : r._kind === "Duplicate" ? "text-amber-500"
                          : r._kind === "Update" ? "text-blue-500"
                          : "text-emerald-500"
                        }>{r._kind}</span>
                      </td>
                      <td className="p-1.5 text-[11px]">{r.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)} disabled={busy}>Back</Button>
              <Button onClick={confirm} disabled={busy || (preview.counts.valid + preview.counts.updates === 0)}
                data-testid="inv-upload-confirm">
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Confirm Import ({preview.counts.valid + preview.counts.updates})
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ============================================================
 * Invoice / Purchase (link an invoice to an existing asset)
 * ============================================================ */
function InvoicePanel({ token, center }) {
  const [assets, setAssets] = useState([]);
  const [f, setF] = useState({
    asset_id: "", invoice_number: "", invoice_date: "", vendor: "",
    added_quantity: 0, purchase_rate: 0, warranty_expiry: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    post("/assets/list", token, { center, limit: 500 })
      .then(d => setAssets((d.assets || []).filter(a => !a.is_raw_material_link)))
      .catch(e => toast.error(e.message));
  }, [token, center]);

  const submit = async () => {
    if (!f.asset_id) return toast.error("Select an asset");
    if (!f.invoice_number) return toast.error("Invoice number is required");
    setBusy(true);
    try {
      await post("/assets/invoice-link", token, f);
      toast.success("Invoice recorded — quantity & rate updated");
      setF({ ...f, invoice_number: "", invoice_date: "", vendor: "", added_quantity: 0,
              purchase_rate: 0, warranty_expiry: "" });
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Record Purchase / Invoice</CardTitle>
        <div className="text-xs text-muted-foreground">
          Pick an existing asset from the register and record the incoming purchase invoice —
          quantity, rate and warranty are all merged into the asset&apos;s live totals with a full audit trail.
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Asset *</Label>
            <Select value={f.asset_id} onValueChange={(v) => setF({ ...f, asset_id: v })}>
              <SelectTrigger data-testid="inv-invoice-asset"><SelectValue placeholder="Search & pick asset" /></SelectTrigger>
              <SelectContent className="max-h-96">
                {assets.map(a => (
                  <SelectItem key={a.asset_id} value={a.asset_id}>
                    {a.asset_code} — {a.asset_name} (current qty {a.quantity_current})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Invoice Number *</Label>
            <Input value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })}
              data-testid="inv-invoice-number" /></div>
          <div><Label>Invoice Date</Label>
            <Input type="date" value={f.invoice_date}
              onChange={(e) => setF({ ...f, invoice_date: e.target.value })} /></div>
          <div><Label>Vendor</Label>
            <Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} /></div>
          <div><Label>Warranty Expiry</Label>
            <Input type="date" value={f.warranty_expiry}
              onChange={(e) => setF({ ...f, warranty_expiry: e.target.value })} /></div>
          <div><Label>Quantity Added</Label>
            <Input type="number" value={f.added_quantity}
              onChange={(e) => setF({ ...f, added_quantity: parseFloat(e.target.value) || 0 })}
              data-testid="inv-invoice-qty" /></div>
          <div><Label>Purchase Rate</Label>
            <Input type="number" value={f.purchase_rate}
              onChange={(e) => setF({ ...f, purchase_rate: parseFloat(e.target.value) || 0 })}
              data-testid="inv-invoice-rate" /></div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={submit} disabled={busy} data-testid="inv-invoice-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Record Invoice
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
